import { decode } from 'jsonwebtoken'
import { CheckAccessToken, refreshAccessToken } from '@/api/session/token'
import { sessionStore as store } from '@/store/session'
import { AnyObject } from '@/models/helpers/common'

const STORAGE_KEY = 'auth'

/** 提前刷新时长 */
const REFRESH_ADVANCE_TIME = 60 * 1000

/**
 * 判断当前 Access Token 是否有效
 */
export function hasValidAccessToken() {
  return isValidToken(store.accessToken)
}

/**
 * 获取 http 校验头, 并触发 token 更新
 */
export async function getAuthorizationHeader() {
  await triggerRefresh()
  return hasValidAccessToken()
    ? { Authorization: `Bearer ${store.accessToken}` }
    : null
}

/**
 * 判断本地是否存在RefreshToken及其有效性
 *
 * 存在且有效 返回 true 否则返回 false
 */
export function CheckLocalRefreshToken(): boolean {
  const refreshToken = store.refreshToken
  if (!refreshToken) return false
  return isValidToken(refreshToken)
}

/**
 * 更新 Token
 * @param accessToken
 * @param refreshToken
 * @param persistence 是否持久化
 */
export function updateTokens(
  accessToken: string,
  refreshToken?: string,
  persistence?: boolean
) {
  store.updateAccessToken(accessToken)
  if (refreshToken) {
    store.updateRefreshToken(refreshToken)
    const payload = { token: refreshToken }
    persistence ? setTokenToSession(payload) : setTokenToLocal(payload)
  }
}

///根据RefreshToken换取AccessToken 并存到 sessionStorage
export async function RefreshAndStoreToken() {
  const RefreshToken = store.refreshToken
  if (RefreshToken != null) {
    const res = await refreshAccessToken(RefreshToken)
    if (res != null) {
      store.updateAccessToken(res.accessToken)
      return true
    } else return false
  } else {
    return false
  }
}

/**
 * 清空 tokens 及其本地储存
 */
export function cleanTokens() {
  store.cleanAccessToken()
  store.cleanRefreshToken()
  sessionStorage.removeItem(STORAGE_KEY)
  localStorage.removeItem(STORAGE_KEY)
}

/**
 * 验证 Access Token 有效性
 */
export async function verify() {
  if (!hasValidAccessToken()) return false

  try {
    await CheckAccessToken()
  } catch (e) {
    return false
  }

  return true
}

/**
 * 判断是否需要刷新 Access Token
 * 已过期时等待刷新
 * 预刷新时不等待
 */
async function triggerRefresh() {
  if (!store.accessToken) return false

  const accessExp = getTokenExpress(store.accessToken).getTime()
  const now = new Date().getTime()

  if (!isValidToken(store.refreshToken)) return false
  if (!isValidToken(store.accessToken)) return await refreshToken()

  const edge = now + REFRESH_ADVANCE_TIME
  const preRefresh = hasValidAccessToken() && accessExp < edge
  if (preRefresh) refreshToken()
  return preRefresh
}

/**
 * 尝试从本地储存获取 Token
 */
export async function updateTokensByStorage() {
  const token = getTokenFromStorage()
  if (token) {
    store.updateRefreshToken(token)
    await refreshToken()
  }
  return !!token
}

async function refreshToken() {
  try {
    const token = store.refreshToken
    const res = await refreshAccessToken(token)
    if (res != null) {
      store.updateAccessToken(res.accessToken)
      return true
    } else return false
  } catch (e) {
    return false
  }
}

function isValidToken(token: string) {
  const exp = getTokenExpress(token).getTime()
  const now = new Date().getTime()
  return now < exp
}

function getTokenExpress(token: string) {
  const data = (decode(token) || {}) as AnyObject
  const exp = 1000 * data.exp || 0
  return new Date(exp)
}

type Payload = { token: string }

function isEffectivePayload(payload: any): payload is Payload {
  if (!payload || !payload.token) return false
  return isValidToken(payload.token)
}

function setTokenToSession(payload: Payload) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
}

function setTokenToLocal(payload: Payload) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
}

function getTokenFromStorage() {
  const data =
    sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY)
  if (!data) return null

  const payload = JSON.parse(data)
  if (!isEffectivePayload(payload)) {
    cleanTokens()
    return null
  }

  return payload.token
}
